import { useState, useRef, useEffect } from "react"
import { Link, router } from "@inertiajs/react"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowLeft, Check, Pencil, X } from "lucide-react"
import { Layout } from "@/components/layouts/Layout"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/motion-tabs"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { RelativeTime } from "@/components/ui/relative-time"
import { Editable, EditableArea, EditablePreview, EditableInput, EditableTrigger, EditableSubmit, EditableCancel } from "@/components/ui/editable"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Overview } from "./Overview"
import { EditView } from "./EditView"
import { SourceEmailSelector } from "./EditSidebar"
import { Settings } from "./Settings"

const TAB_ORDER = ["overview", "edit", "settings"]

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 16 : -16, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -16 : 16, opacity: 0 }),
}

export default function AgentsShow(props) {
  return <Layout><AgentsShowContent {...props} /></Layout>
}

function AgentsShowContent({ agent, workspace_slug, workspaces = [], mode = "overview", runs = [], chart_runs = [], pagination = {}, filters = {} }) {
  const analysisInProgress = agent.analysis_status === "pending" || agent.analysis_status === "analyzing"
  const extractionInProgress = agent.preview_extraction_status === "pending" || agent.preview_extraction_status === "extracting"
  const [activeView, setActiveView] = useState(mode)
  const [runTestCount, setRunTestCount] = useState(0)
  const [scrolled, setScrolled] = useState(false)
  const [hasScrolled, setHasScrolled] = useState(false)
  const [tabsStuck, setTabsStuck] = useState(false)
  const directionRef = useRef(0)
  const scrollContainerRef = useRef(null)
  const nameRef = useRef(null)
  const tabsRef = useRef(null)

  useEffect(() => {
    const root = scrollContainerRef.current
    const target = nameRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(target)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const root = scrollContainerRef.current
    const tabs = tabsRef.current
    if (!root || !tabs) return

    const observer = new IntersectionObserver(
      ([entry]) => setTabsStuck(!entry.isIntersecting),
      { root, threshold: 0 }
    )

    observer.observe(tabs)
    return () => observer.disconnect()
  }, [])

  function handleScrollToTop() {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleScroll(e) {
    setHasScrolled(e.currentTarget.scrollTop > 0)
  }

  function handleRunTest() {
    setRunTestCount(c => c + 1)
    router.post(`/${workspace_slug}/agents/${agent.id}/extract_preview`, {}, { preserveScroll: true })
  }

  function handleStatusToggle(checked) {
    router.patch(`/${workspace_slug}/agents/${agent.id}`, {
      agent: { status: checked ? "active" : "draft" }
    }, { preserveState: true })
  }

  function handleViewChange(view) {
    const oldIndex = TAB_ORDER.indexOf(activeView)
    const newIndex = TAB_ORDER.indexOf(view)
    directionRef.current = newIndex > oldIndex ? 1 : -1
    setActiveView(view)
    setScrolled(false)
    setHasScrolled(false)
    setTabsStuck(false)
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "instant" })

    let newUrl = `/${workspace_slug}/agents/${agent.id}`
    if (view === "edit") {
      newUrl = `/${workspace_slug}/agents/${agent.id}/edit`
    } else if (view === "settings") {
      newUrl = `/${workspace_slug}/agents/${agent.id}/settings`
    }
    window.history.replaceState({}, "", newUrl)
  }

  function handleNameSubmit(name) {
    if (name && name !== agent.name) {
      router.patch(`/${workspace_slug}/agents/${agent.id}`, { agent: { name } }, { preserveState: true })
    }
  }

  function handleSubmit(data) {
    router.patch(`/${workspace_slug}/agents/${agent.id}`, { agent: data }, { preserveState: true })
  }

  return (
    <>

      <header className={`flex items-center justify-between gap-4 px-4 h-16 shrink-0 transition-[width,height,border-color] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 border-b ${hasScrolled ? "border-border" : "border-transparent"}`}>
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" asChild className="group/back -ml-1 h-7 w-7">
                <Link href={`/${workspace_slug}/agents`}>
                  <ArrowLeft className="h-4 w-4 text-muted-foreground group-hover/back:text-foreground" />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>All agents</TooltipContent>
          </Tooltip>
          <AnimatePresence>
            {scrolled && (
              <motion.button
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                onClick={handleScrollToTop}
                className="flex items-center gap-1.5 min-w-0 rounded-md px-2 h-7 hover:bg-accent transition-colors cursor-pointer"
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <AnimatePresence>
                    {agent.status === "active" && (
                      <motion.span
                        key="ping"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.75 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="animate-ping [animation-duration:2s] absolute inline-flex h-full w-full rounded-full bg-green-400"
                      />
                    )}
                  </AnimatePresence>
                  <motion.span
                    animate={{ backgroundColor: agent.status === "active" ? "#22c55e" : "rgb(100 116 139 / 0.4)" }}
                    transition={{ duration: 0.3 }}
                    className="relative inline-flex h-2 w-2 rounded-full"
                  />
                </span>
                <span className="text-sm font-medium truncate">{agent.name || "Untitled agent"}</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
        <div className="flex items-center">
          <AnimatePresence>
            {tabsStuck && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <Tabs value={activeView} onValueChange={handleViewChange}>
                  <TabsList>
                    <TabsTrigger value="overview" className="cursor-pointer">Overview</TabsTrigger>
                    <TabsTrigger value="edit" className="cursor-pointer">Edit</TabsTrigger>
                    <TabsTrigger value="settings" className="cursor-pointer">Settings</TabsTrigger>
                  </TabsList>
                </Tabs>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex items-center">
          <AnimatePresence>
            {tabsStuck && activeView === "edit" && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="flex items-center gap-2"
              >
                {agent.source_emails?.length > 0 && (
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Test agent</span>
                )}
                <div className="flex items-center">
                  {agent.source_emails?.length > 0 && (
                    <SourceEmailSelector agent={agent} workspace_slug={workspace_slug} />
                  )}
                  {agent.data_schema?.fields?.length > 0 && !analysisInProgress && (
                    <Button
                      variant="outline"
                      className={agent.source_emails?.length > 0 ? "rounded-l-none -ml-px relative hover:z-10 focus:z-10" : ""}
                      disabled={extractionInProgress}
                      onClick={handleRunTest}
                    >
                      {extractionInProgress ? "Testing..." : "Run test"}
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      <ScrollArea className="flex-1 min-h-0" viewportRef={scrollContainerRef} onScroll={handleScroll}>

        <div ref={nameRef} className="px-6 pt-6 pb-4">
          <Editable
            key={agent.name || ""}
            defaultValue={agent.name}
            onSubmit={handleNameSubmit}
            placeholder="Untitled agent"
          >
            <div className="flex items-center gap-1">
              <div className="group/editable flex items-center gap-1.5">
                <EditableArea>
                  <EditablePreview className="text-3xl font-medium" />
                  <EditableInput className="text-3xl font-medium border-none shadow-none focus-visible:ring-0" />
                </EditableArea>
                <EditableTrigger asChild>
                  <Button variant="ghost" size="sm" className="group/edit opacity-0 group-hover/editable:opacity-100 transition-all h-7 w-7 p-0 mt-0.25">
                    <Pencil className="h-4 w-4 text-muted-foreground group-hover/edit:text-foreground transition-all" />
                  </Button>
                </EditableTrigger>
              </div>
              <EditableSubmit asChild>
                <Button variant="ghost" size="sm" className="group/submit h-7 w-7 p-0 hover:bg-green-100 transition-all">
                  <Check className="h-4 w-4 text-muted-foreground group-hover/submit:text-green-500 transition-all" />
                </Button>
              </EditableSubmit>
              <EditableCancel asChild>
                <Button variant="ghost" size="sm" className="group/cancel h-7 w-7 p-0 transition-all">
                  <X className="h-4 w-4 text-muted-foreground group-hover/cancel:text-foreground transition-all" />
                </Button>
              </EditableCancel>
            </div>
          </Editable>
          <div className="flex items-center gap-2 mt-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  layout
                  onClick={() => handleStatusToggle(agent.status !== "active")}
                  transition={{ layout: { duration: 0.3 } }}
                  className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-sm font-medium cursor-pointer transition-colors duration-300 ${
                    agent.status === "active"
                      ? "bg-green-100 text-green-600 hover:text-green-700"
                      : "bg-muted text-slate-500 hover:text-foreground"
                  }`}
                >
                  <span className="relative flex h-2 w-2 shrink-0">
                    <AnimatePresence>
                      {agent.status === "active" && (
                        <motion.span
                          key="ping"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.75 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.4 }}
                          className="animate-ping [animation-duration:2s] absolute inline-flex h-full w-full rounded-full bg-green-400"
                        />
                      )}
                    </AnimatePresence>
                    <motion.span
                      animate={{ backgroundColor: agent.status === "active" ? "#22c55e" : "rgb(100 116 139 / 0.4)" }}
                      transition={{ duration: 0.3 }}
                      className="relative inline-flex h-2 w-2 rounded-full"
                    />
                  </span>
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={agent.status}
                      initial={{ opacity: 0, y: 3 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -3 }}
                      transition={{ duration: 0.15 }}
                    >
                      {agent.status === "active" ? "Active" : "Paused"}
                    </motion.span>
                  </AnimatePresence>
                </motion.button>
              </TooltipTrigger>
              <TooltipContent>
                {agent.status === "active" ? "Turn off agent" : "Turn on agent"}
              </TooltipContent>
            </Tooltip>
            <RelativeTime createdAt={agent.created_at} updatedAt={agent.updated_at} />
          </div>
        </div>

        <div ref={tabsRef} className="px-6 py-4 flex items-center gap-8 justify-between">
          <Tabs value={activeView} onValueChange={handleViewChange}>
            <TabsList>
              <TabsTrigger value="overview" className="cursor-pointer">Overview</TabsTrigger>
              <TabsTrigger value="edit" className="cursor-pointer">Edit</TabsTrigger>
              <TabsTrigger value="settings" className="cursor-pointer">Settings</TabsTrigger>
            </TabsList>
          </Tabs>
          <AnimatePresence>
            {activeView === "edit" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="flex items-center gap-2"
            >
              {agent.source_emails?.length > 0 && (
                <span className="text-sm text-muted-foreground whitespace-nowrap">Test agent</span>
              )}
              <div className="flex items-center">
                {agent.source_emails?.length > 0 && (
                  <SourceEmailSelector agent={agent} workspace_slug={workspace_slug} />
                )}
                {agent.data_schema?.fields?.length > 0 && !analysisInProgress && (
                  <Button
                    variant="outline"
                    className={agent.source_emails?.length > 0 ? "rounded-l-none -ml-px relative hover:z-10 focus:z-10" : ""}
                    disabled={extractionInProgress}
                    onClick={handleRunTest}
                  >
                    {extractionInProgress ? "Testing..." : "Run test"}
                  </Button>
                )}
              </div>
            </motion.div>
          )}
          </AnimatePresence>
        </div>

        <div className="relative overflow-hidden [contain:inline-size]">
          <AnimatePresence initial={false} mode="popLayout" custom={directionRef.current}>

            {activeView === "overview" && (
              <motion.div
                key="overview"
                custom={directionRef.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="w-full"
              >
                <div className="flex flex-col gap-8 px-6 pt-4 pb-6">
                  <Overview agent={agent} workspace_slug={workspace_slug} runs={runs} chart_runs={chart_runs} pagination={pagination} filters={filters} />
                </div>
              </motion.div>
            )}

            {activeView === "edit" && (
              <motion.div
                key="edit"
                custom={directionRef.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="w-full"
              >
                <EditView
                  agent={agent}
                  workspace_slug={workspace_slug}
                  onSubmit={handleSubmit}
                  scrollContainerRef={scrollContainerRef}
                  runTestCount={runTestCount}
                />
              </motion.div>
            )}

            {activeView === "settings" && (
              <motion.div
                key="settings"
                custom={directionRef.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="w-full"
              >
                <div className="flex flex-col gap-8 px-6 pt-4 pb-6">
                  <Settings
                    agent={agent}
                    workspace_slug={workspace_slug}
                    workspaces={workspaces}
                  />
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

      </ScrollArea>

      {activeView === "edit" && (
        <footer className="flex px-4 h-16 shrink-0 items-center gap-2 border-t transition-[width,height] ease-out group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
          <Button type="submit" form="agent-edit-form">
            Update agent
          </Button>
        </footer>
      )}

    </>
  )
}
